import { useState, JSX } from "react";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, IHostWebsite, IWebsiteEvent, OmneHostService, SanitizedResponse } from "../../../../services";
import React from "react";
import { Classname, DateHelper, Paginator, Scheme, useSearchParams } from "@rivtn/noseur";
import { Utils } from "../../../../utils";

export function buildEventView(ev: IWebsiteEvent) {

    let status = "upcoming";
    if (ev.start_date && DateHelper.isPast(new Date(ev.start_date))) status = "ongoing";
    if (ev.end_date && DateHelper.isPast(new Date(ev.end_date))) status = "past";

    return (<div key={ev.external_id} className={styles["event-card"]}>
        {(!!ev.media_location) && (<img className={styles["event-flier"]} src={ev.media_location} alt={ev.title} loading="lazy" />)}
        <div className={styles["event-body"]}>
            <div className={styles["event-name"]}>{ev.title}</div>
            {(!!ev.details) && (<div className={styles["event-desc"]}>{ev.details}</div>)}
            {(!!ev.location) && (<div style={{ color: "var(--muted)", fontSize: "0.75rem", marginBottom: "0.75rem" }}>📍 {ev.location}</div>)}

            <div className={styles["event-footer"]}>
                {(!!ev.start_date) && (<div className={styles["event-date"]}>
                    <span className={`${styles["event-status-dot"]} ${styles[`dot-${status}`]}`} />📅 {Utils.fineDateShort(new Date(ev.start_date))}
                </div>)}
                {(status === "past") && (<span style={{ fontSize: "0.72rem", color: "var(--muted)" }}>Concluded</span>)}
                {(ev.action_label || ev.action_location) && (<ReactRouterDom.NavLink to={ev.action_location} target="_blank" style={{ textDecoration: "none" }} className={styles["btn-register"]}>{ev.action_label ?? ev.action_location}</ReactRouterDom.NavLink>)}
            </div>
        </div>
    </div>);
}

interface EventsProps {
    website: IHostWebsite;
}

function Events(props: EventsProps) {

    const { website } = props;
    const omnehostService = OmneHostService.getInstance();
    const [events, setEvents] = React.useState<ApiResponseData<IWebsiteEvent>>();
    const { page, setPage, tab, setTab } = useSearchParams({ defaultInit: window.location.search, params: { page: 1, tab: "all" } });

    React.useEffect(fetchInformation, [omnehostService, page, tab]);

    return (
        <div className={styles["section"]}>
            <div className={styles["sec-line"]} />
            <h1 className={styles["sec-title"]}>Our <em>Events</em></h1>
            <p className={styles["sec-sub"]}>Upcoming, ongoing and past events</p>

            <div className={styles["tabs"]}>
                {(([
                    ["all", "All Events"],
                    ["upcoming", "Upcoming"],
                    ["ongoing", "Live"],
                    ["past", "Past"]
                ] as [string, string][]).map(([v, l]) => (
                    <button
                        key={v}
                        className={`${styles["tab-btn"]} ${tab === v ? styles["active"] : ""}`}
                        onClick={() => setTab(v as string)}
                    >
                        {l}
                    </button>
                )))}
            </div>

            {(!!events?.content)
                ? ((events.content && events.content?.length > 0)
                    ? (<div className={styles["events-grid"]}>
                        {events.content!.map(buildEventView)}
                    </div>)
                    : (Utils.emptyState()))
                : (Utils.loadingState())}

            <Paginator scheme={Scheme.LIGHT} style={{ marginTop: 50 }} rowsPerPage={events?.size} totalRecords={events?.total_elements} initialPage={parseInt("" + page)} onPageChange={(e) => {
                setPage(e.currentPage)
            }} template={{
                activePageLabel: () => "",
                nextPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                previousPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                lastPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                firstPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                pageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, (option.active ? styles["active"] : null))} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick}>{option.label}</button>),
            }} />
        </div>
    );

    function fetchInformation() {
        setEvents({});
        const extraParams: any = {};
        if (tab === "past") { extraParams.end_date__lt_eq = DateHelper.now().toISOString(); }
        else if (tab === "upcoming") { extraParams.start_date__gt_eq = DateHelper.now().toISOString(); }
        else if (tab === "ongoing") {
            extraParams.end_date__gt_eq = DateHelper.now().toISOString();
            extraParams.start_date__lt_eq = DateHelper.now().toISOString();
        }
        omnehostService.queryHostEvents(website.host.external_id, {
            page,
            ...extraParams,
            sort: "updated_at,DESC",
        }).then(({ apiResponse }: SanitizedResponse<IWebsiteEvent>) => {
            setEvents(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default Events;